import React, { useMemo } from 'react';
import { formatDate } from '../../utils/dateUtils';
import { groupEventsIntoSessions } from '../../utils/verificationSessions';

const EVENT_LABELS = {
  code_sent: { text: 'Code sent', badge: 'bg-primary' },
  code_send_failed: { text: 'Send failed', badge: 'bg-danger' },
  code_send_blocked_opted_out: { text: 'Blocked - opted out', badge: 'bg-warning text-dark' },
  code_send_blocked_no_phone: {
    text: 'Blocked - nothing on file', badge: 'bg-warning text-dark'
  },
  code_send_blocked_no_destination: {
    text: 'Blocked - nothing on file', badge: 'bg-warning text-dark'
  },
  code_send_blocked_destination: {
    text: 'Blocked - address refused', badge: 'bg-warning text-dark'
  },
  code_send_rate_limited: { text: 'Blocked - too many codes', badge: 'bg-warning text-dark' },
  verify_succeeded: { text: 'Verified', badge: 'bg-success' },
  verify_failed_wrong_code: { text: 'Wrong code', badge: 'bg-danger' },
  verify_failed_expired: { text: 'Expired', badge: 'bg-secondary' },
  verify_failed_not_found: { text: 'No longer valid', badge: 'bg-secondary' },
  verify_failed_attempts_exceeded: { text: 'Locked out', badge: 'bg-danger' },
  code_cancelled: { text: 'Cancelled', badge: 'bg-secondary' }
};

/**
 * How the destination reads in the table.
 *
 * The audit trail stores only a hint - last four digits for a phone, the domain
 * for an email - so this formats that hint rather than masking a full value it
 * never receives.
 */
const describeDestination = (channel, hint) => {
  if (!hint) return '-';
  if (channel === 'email') return `•••@${String(hint).replace(/^@/, '')}`;
  return `•••${hint}`;
};

const CHANNEL_ICONS = {
  phone: 'bi-phone',
  email: 'bi-envelope'
};

/**
 * The verification audit trail for one client, one row per verification.
 *
 * A single call writes several audit events - a send, some wrong codes, a
 * success - which as separate rows read like several separate calls. They are
 * folded here into one row showing the latest outcome and the note captured on
 * the send. Blocked sends create no verification, so each stays its own row
 * rather than disappearing under an unrelated session.
 *
 * Memoized deliberately. Rendering costs an Intl date format per row, and
 * without the boundary every keystroke in the code boxes re-formatted all of
 * them. Keep the props shallow-comparable: pass primitives and the `events`
 * array, never the verification object, whose identity churns.
 *
 * The rows are never swapped for a spinner once they exist. The page updates
 * this list by merging the audit record each mutation returns, so there is no
 * refetch to show progress for - and unmounting the table would collapse the
 * card and throw away the scroll position mid-call.
 */
const VerificationHistory = React.memo(({ clientName = '', events = [], loading = false }) => {
  const hasSelection = Boolean(clientName);

  // Recomputed only when the events array identity changes, which is on each
  // merged audit record rather than on every parent render.
  const sessions = useMemo(() => groupEventsIntoSessions(events), [events]);

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <strong>Verification history</strong>
        {hasSelection && <span className="text-muted small">{clientName}</span>}
      </div>
      <div className="card-body p-0">
        {!hasSelection && (
          <div className="p-3 text-muted">Choose a client to see their verification history.</div>
        )}

        {hasSelection && loading && (
          <div className="p-3 text-muted">
            <span className="spinner-border spinner-border-sm me-2"></span>
            Loading history...
          </div>
        )}

        {hasSelection && !loading && sessions.length === 0 && (
          <div className="p-3 text-muted">No verification activity recorded yet.</div>
        )}

        {hasSelection && !loading && sessions.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Outcome</th>
                  <th>Sent to</th>
                  <th className="text-center">Attempts</th>
                  <th>Staff</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => {
                  const label = EVENT_LABELS[session.eventType] || {
                    text: session.eventType,
                    badge: 'bg-secondary'
                  };
                  const icon = CHANNEL_ICONS[session.channel];
                  return (
                    <tr key={session.key}>
                      <td className="text-nowrap">{formatDate(session.createdAt)}</td>
                      <td>
                        <span className={`badge ${label.badge}`}>{label.text}</span>
                      </td>
                      <td className="text-nowrap">
                        {icon && <i aria-hidden="true" className={`bi ${icon} me-1 text-muted`}></i>}
                        <span className="font-monospace">
                          {describeDestination(session.channel, session.destinationHint)}
                        </span>
                      </td>
                      <td className="text-center">
                        {session.attempts > 0 ? session.attempts : ''}
                      </td>
                      <td>{session.staffDisplayName || '-'}</td>
                      <td>{session.reason || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
});

VerificationHistory.displayName = 'VerificationHistory';

export default VerificationHistory;
